import { slugify } from "./slug";

// Canonical shape for a single question. Keeps every record consistent
// regardless of where it came from (seed JSON, DB, import, custom add).
export function normalizeQuestion(q, topicName) {
	q = q || {};
	return {
		Topic: q.Topic || topicName || "",
		Problem: q.Problem || "",
		Done: !!q.Done,
		Bookmark: q.Bookmark || false,
		Notes: typeof q.Notes === "string" ? q.Notes : "",
		Revise: !!q.Revise,
		Difficulty: q.Difficulty || "",
		SolvedAt: q.SolvedAt || null,
		// seconds spent solving (recorded by the Practice / Mock timer)
		TimeSpent: typeof q.TimeSpent === "number" && q.TimeSpent > 0 ? q.TimeSpent : 0,
		// spaced-repetition schedule: next review day (YYYY-MM-DD) + ladder stage
		ReviseAt: q.ReviseAt || null,
		ReviseStage: typeof q.ReviseStage === "number" && q.ReviseStage > 0 ? q.ReviseStage : 0,
		URL: q.URL || "",
		URL2: q.URL2 || "",
		Custom: !!q.Custom,
	};
}

// Recompute the derived progress fields for a topic from its questions.
export function recomputeTopic(topic) {
	const questions = (topic.questions || []).map((q) => normalizeQuestion(q, topic.topicName));
	const doneQuestions = questions.filter((q) => q.Done).length;
	return {
		topicName: topic.topicName,
		position: topic.position,
		started: doneQuestions > 0 || !!topic.started,
		doneQuestions,
		questions,
		Custom: !!topic.Custom,
	};
}

// Per-question fields that represent *user state* (must survive seed updates).
const USER_FIELDS = ["Done", "Bookmark", "Notes", "Revise", "Difficulty", "SolvedAt", "TimeSpent", "ReviseAt", "ReviseStage"];

// Non-destructive merge of the seed list (jsonTopics) with whatever is already
// saved in the DB (dbTopics). The seed defines the canonical problems/order;
// the DB contributes the user's progress plus any custom topics/problems.
//
// Guarantees:
//  - User state on canonical questions is preserved (matched by index).
//  - Custom questions appended beyond the canonical length are kept.
//  - Whole custom topics that only exist in the DB are kept.
//  - Output positions are reassigned to match final array order.
export function mergeTopicData(jsonTopics, dbTopics) {
	jsonTopics = jsonTopics || [];
	dbTopics = dbTopics || [];

	const dbBySlug = new Map();
	dbTopics.forEach((t) => {
		if (t && t.topicName != null) dbBySlug.set(slugify(t.topicName), t);
	});

	const seenSlugs = new Set();
	const merged = [];

	jsonTopics.forEach((jt) => {
		const slug = slugify(jt.topicName);
		seenSlugs.add(slug);
		const dbt = dbBySlug.get(slug);
		const dbQs = (dbt && dbt.questions) || [];
		const canonicalLen = jt.questions.length;

		// 1) canonical questions, overlaid with saved user state (by index)
		const questions = jt.questions.map((q, qi) => {
			const saved = dbQs[qi] || {};
			const out = normalizeQuestion(q, jt.topicName);
			USER_FIELDS.forEach((f) => {
				if (saved[f] !== undefined && saved[f] !== null && saved[f] !== "") {
					out[f] = saved[f];
				}
			});
			return out;
		});

		// 2) custom questions the user appended after the canonical block
		for (let qi = canonicalLen; qi < dbQs.length; qi++) {
			questions.push(normalizeQuestion({ ...dbQs[qi], Custom: true }, jt.topicName));
		}

		merged.push(recomputeTopic({ ...jt, questions, Custom: !!(dbt && dbt.Custom) }));
	});

	// 3) DB-only custom topics (not present in the seed list)
	dbTopics.forEach((t) => {
		if (!t || t.topicName == null) return;
		const slug = slugify(t.topicName);
		if (seenSlugs.has(slug)) return;
		seenSlugs.add(slug);
		merged.push(recomputeTopic({ ...t, Custom: true }));
	});

	// 4) stable, contiguous positions
	merged.forEach((t, i) => {
		t.position = i;
	});

	return merged;
}

export default mergeTopicData;
